/**
 * Page 4: settings, encrypted secrets and saved browser sessions (`/settings`).
 *
 * Secrets are sealed **in the browser** with the unlocked `taskjinn` HKDF subkey and only the
 * ciphertext envelope is sent to the worker. The vault key comes from `useAppVault` (sessionStorage)
 * so a reload in the same tab does not force another Passkey ceremony.
 */

import { useCallback, useEffect, useState } from "react";
import {
  decryptAppSecretWithAppKey,
  encryptAppSecretWithAppKey,
  type EncryptedSecretEnvelope,
  type EncryptedSessionEnvelope,
  type SecretCategory,
} from "@tensilelabs/auth-client";
import { useAuth, APP_ID } from "../auth/AuthContext";
import {
  deleteVaultSecret,
  deleteVaultSession,
  fetchVaultSecrets,
  fetchVaultSessions,
  putVaultSecret,
} from "../api";

/**
 * Creation categories.
 *
 * `deepseek_api_key` is deliberately absent (Phase 8A A8): the MVP runs on the platform key, and the
 * tenant-funded model is opt-in and unexercised. The **storage path stays** — the enum, the PKI
 * envelope and the ciphertext are unchanged — so an existing row keeps listing, revealing and
 * deleting. Only the option to create a new one is hidden, and the list renders
 * `secret.label || secret.secret_id`, so nothing is orphaned.
 */
const CATEGORIES: Array<{ value: SecretCategory; label: string }> = [
  { value: "payment_card", label: "Payment / card information" },
  { value: "identity_info", label: "Identity / profile information" },
  { value: "agent_dynamic", label: "Agent-managed dynamic field" },
  { value: "custom", label: "Other secret" },
];

export function SettingsVaultPage() {
  const auth = useAuth();
  const { session, appScopedSecretKeyB64, vaultUnlocked, isAdmin } = auth;
  const [secrets, setSecrets] = useState<EncryptedSecretEnvelope[]>([]);
  const [sessions, setSessions] = useState<EncryptedSessionEnvelope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<{ id: string; value: string } | null>(null);

  // Neutral defaults: a prefilled "DeepSeek API key" would nudge a tenant toward a credential the
  // product no longer asks for, which is a worse outcome than an empty field.
  const [secretId, setSecretId] = useState("");
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState<SecretCategory>("custom");
  const [isAgentDynamic, setIsAgentDynamic] = useState(false);
  const [plaintext, setPlaintext] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [secretsResponse, sessionsResponse] = await Promise.all([
        fetchVaultSecrets(),
        fetchVaultSessions(),
      ]);
      setSecrets(secretsResponse.secrets);
      setSessions(sessionsResponse.sessions);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not load the vault");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (session) void reload();
  }, [session, reload]);

  const addSecret = async () => {
    if (!session || !appScopedSecretKeyB64) return;
    setPending("save");
    setError(null);
    try {
      const envelope = await encryptAppSecretWithAppKey({
        appScopedSecretKeyB64,
        accountId: session.accountId ?? "",
        appId: APP_ID,
        secretId: secretId.trim(),
        label: label.trim() || secretId.trim(),
        category,
        isAgentDynamic,
        plaintext,
      });
      await putVaultSecret(envelope);
      setPlaintext("");
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the secret");
    } finally {
      setPending(null);
    }
  };

  const reveal = async (envelope: EncryptedSecretEnvelope) => {
    if (!appScopedSecretKeyB64) {
      setError("Unlock the vault with your Passkey to reveal secrets.");
      return;
    }
    setPending(envelope.secret_id);
    setError(null);
    try {
      const value = await decryptAppSecretWithAppKey({
        appScopedSecretKeyB64,
        envelope,
      });
      setRevealed({ id: envelope.secret_id, value });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not decrypt the secret");
    } finally {
      setPending(null);
    }
  };

  const removeSecret = async (id: string) => {
    setPending(id);
    setError(null);
    try {
      await deleteVaultSecret(id);
      if (revealed?.id === id) setRevealed(null);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not delete the secret");
    } finally {
      setPending(null);
    }
  };

  const removeSession = async (domain: string) => {
    setPending(domain);
    setError(null);
    try {
      await deleteVaultSession(domain);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not clear the session");
    } finally {
      setPending(null);
    }
  };

  if (!session) return null;

  return (
    <main className="tj-container max-w-4xl space-y-4 py-6" data-testid="settings-vault-page">
      <header>
        <p className="text-xs uppercase tracking-wide text-[var(--tj-muted)]">Settings</p>
        <h1 className="serif-heading text-2xl">Secrets &amp; encrypted sessions</h1>
        <p className="mt-1 text-sm text-[var(--tj-muted)]">
          Everything below is sealed client-side under{" "}
          <code className="font-mono text-xs">tensilelabs:vault:taskjinn:v1</code> before it leaves
          the browser.
        </p>
      </header>

      <div
        className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3 text-sm ${
          vaultUnlocked
            ? "border-[var(--tj-border)] bg-[var(--tj-surface)]"
            : "border-amber-300 bg-amber-50 text-amber-900"
        }`}
      >
        <span>
          {vaultUnlocked
            ? `Vault unlocked${auth.identityFingerprint ? ` · ${auth.identityFingerprint}` : ""}`
            : "Vault locked — unlock with your Passkey to add or reveal secrets."}
        </span>
        {!vaultUnlocked ? (
          <button
            type="button"
            className="tj-btn tj-btn-primary"
            disabled={auth.busy}
            onClick={() => void auth.unlockVault()}
          >
            Unlock vault
          </button>
        ) : null}
      </div>

      {!isAdmin ? (
        <p className="tj-badge tj-badge-amber block rounded-md p-3">
          Run-only role: you can view the vault, but only Owners and Admins can add or delete
          secrets.
        </p>
      ) : null}
      {auth.error ? (
        <p className="tj-badge tj-badge-red block rounded-md p-3" role="alert">
          {auth.error}
        </p>
      ) : null}
      {error ? (
        <p className="tj-badge tj-badge-red block rounded-md p-3" role="alert">
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-4">
          <h2 className="serif-heading text-lg">Encrypted secrets ({secrets.length})</h2>
          {loading ? (
            <p className="mt-2 text-sm text-[var(--tj-muted)]">Loading…</p>
          ) : secrets.length === 0 ? (
            <p className="mt-2 text-sm text-[var(--tj-muted)]">No secrets stored yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-[var(--tj-border)]">
              {secrets.map((secret) => (
                <li key={secret.secret_id} className="space-y-1 py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium">{secret.label || secret.secret_id}</p>
                      <p className="font-mono text-xs text-[var(--tj-muted)]">
                        {secret.secret_id} · {secret.category}
                        {secret.is_agent_dynamic ? " · agent dynamic" : ""}
                      </p>
                    </div>
                    <span className="tj-badge tj-badge-green">PKI encrypted</span>
                  </div>
                  {revealed?.id === secret.secret_id ? (
                    <div className="rounded-md bg-[var(--tj-bg)] p-2">
                      <p className="break-all font-mono text-xs" data-testid="revealed-secret">
                        {revealed.value}
                      </p>
                      <button
                        type="button"
                        className="mt-1 text-xs underline"
                        onClick={() => setRevealed(null)}
                      >
                        Hide
                      </button>
                    </div>
                  ) : null}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="tj-btn tj-btn-secondary"
                      disabled={pending === secret.secret_id}
                      onClick={() => void reveal(secret)}
                    >
                      Reveal
                    </button>
                    {isAdmin ? (
                      <button
                        type="button"
                        className="tj-btn tj-btn-secondary"
                        disabled={pending === secret.secret_id}
                        onClick={() => void removeSecret(secret.secret_id)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {isAdmin ? (
            <form
              className="mt-4 space-y-2 border-t border-[var(--tj-border)] pt-4"
              onSubmit={(event) => {
                event.preventDefault();
                if (!vaultUnlocked) {
                  setError("Unlock the vault before adding a secret.");
                  return;
                }
                void addSecret();
              }}
            >
              <h3 className="text-sm font-semibold">Add a secret</h3>
              <label className="block text-sm">
                Key
                <input
                  required
                  value={secretId}
                  onChange={(event) => setSecretId(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2 font-mono text-xs"
                />
              </label>
              <label className="block text-sm">
                Label
                <input
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2"
                />
              </label>
              <label className="block text-sm">
                Category
                <select
                  value={category}
                  onChange={(event) => setCategory(event.target.value as SecretCategory)}
                  className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2"
                >
                  {CATEGORIES.map((entry) => (
                    <option key={entry.value} value={entry.value}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isAgentDynamic}
                  onChange={(event) => setIsAgentDynamic(event.target.checked)}
                />
                Managed dynamically by the agent
              </label>
              <label className="block text-sm">
                Value
                <textarea
                  required
                  rows={3}
                  value={plaintext}
                  onChange={(event) => setPlaintext(event.target.value)}
                  placeholder="Never sent in plaintext"
                  className="mt-1 w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-bg)] px-3 py-2 font-mono text-xs"
                />
              </label>
              <button
                type="submit"
                className="tj-btn tj-btn-primary"
                disabled={pending === "save" || !vaultUnlocked}
              >
                Seal &amp; save
              </button>
            </form>
          ) : null}
        </section>

        <section className="rounded-xl border border-[var(--tj-border)] bg-[var(--tj-surface)] p-4">
          <h2 className="serif-heading text-lg">Saved browser sessions ({sessions.length})</h2>
          <p className="mt-1 text-xs text-[var(--tj-muted)]">
            Sealed CloakBrowser <code>storageState</code> bundles are captured after a VNC login and
            restored on the next run for the same domain.
          </p>
          {loading ? (
            <p className="mt-2 text-sm text-[var(--tj-muted)]">Loading…</p>
          ) : sessions.length === 0 ? (
            <p className="mt-2 text-sm text-[var(--tj-muted)]">No saved sessions yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-[var(--tj-border)]">
              {sessions.map((stored) => (
                <li
                  key={stored.domain}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <div>
                    <p className="text-sm font-medium">{stored.domain}</p>
                    <p className="text-xs text-[var(--tj-muted)]">
                      {stored.cookie_count} cookies · updated{" "}
                      {new Date(stored.updated_at).toLocaleString()}
                    </p>
                  </div>
                  {isAdmin ? (
                    <button
                      type="button"
                      className="tj-btn tj-btn-secondary"
                      disabled={pending === stored.domain}
                      onClick={() => void removeSession(stored.domain)}
                    >
                      Clear
                    </button>
                  ) : (
                    <span className="tj-badge tj-badge-green">Restorable</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 rounded-lg border border-dashed border-[var(--tj-border)] p-3 text-xs text-[var(--tj-muted)]">
            Model routing (Pro/High for builder &amp; review, Flash/Medium for execution) is
            configured on PVR3; this page never holds a model credential in plaintext.
          </div>
        </section>
      </div>
    </main>
  );
}
